import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Wallet,
  Clock,
  DollarSign,
  Users,
  AlertCircle,
  X,
  Loader2,
  Check,
  Square,
  Search,
  Moon,
  Eye,
  Calendar,
  BarChart2,
  ChevronDown,
  MapPin,
} from "lucide-react";
import Layout from "../../components/layout/Layout";
import MainHeading from "../../components/common/MainHeading";
import TablePagination from "../../components/common/TablePagination";
import { Button } from "../../components/ui/button";
import { ThrowError } from "../../api/callApi";
import { RoutesLink } from "../../utility/RouteLinks";
import type { IPageInfo } from "./Wages";
import DateRangePicker from "../../components/common/DateRangePicker";

// ─── Types ────────────────────────────────────────────────────────────────

interface IAttendanceDay {
  date: number; // day of month 1-31
  inTime: string | null; // "8:00 AM" or null if absent
  outTime: string | null;
  lunchBreak: number; // in hours
  workingTime: number; // in hours
  regularWorkTime: number; // in hours
  paid: boolean;
}

interface ISite {
  id: string;
  name: string;
}

interface IMonthlyEmployee {
  id: string;
  name: string;
  designation?: string;
  site: ISite;
  dailyRate: number;
  payCycleDays: number;
  attendance: IAttendanceDay[];
  summary: {
    regularWageHour: number;
    otWageHour: number;
    regularHoursPayment: number;
    otPayment: number;
    netPayment: number;
    totalWorkingDays: number;
    totalHours: number;
    totalOTHours: number;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────

const inr = (value: number) =>
  `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month, 0).getDate();
};

const getMonthName = (month: number) => {
  return new Date(0, month - 1).toLocaleString("default", { month: "long" });
};

/** "8:00 AM" -> "8:00a", "4:30 PM" -> "4:30p" — compact for day cards */
const compactTime = (time: string | null) => {
  if (!time) return "";
  const [hm, meridiem] = time.split(" ");
  const [h, m] = hm.split(":");
  const suffix = meridiem === "AM" ? "a" : "p";
  return `${h}:${m}${suffix}`;
};

/** Format break hours to text e.g., 0.5 -> "30m", 1 -> "1h" */
const formatBreak = (hours: number) => {
  if (hours <= 0) return "0m";
  const mins = Math.round(hours * 60);
  return mins >= 60 ? `${hours}h` : `${mins}m`;
};

/** Get Left Border Color class based on Employee Designation */
const getDesignationColor = (designation?: string) => {
  const d = designation?.toLowerCase() || "";
  if (d.includes("mason")) return "border-l-[#6366f1]"; // Purple/Indigo
  if (d.includes("helper")) return "border-l-[#f97316]"; // Orange
  if (d.includes("supervisor")) return "border-l-[#ec4899]"; // Pink
  if (d.includes("carpenter")) return "border-l-[#3b82f6]"; // Blue
  return "border-l-teal-500"; // Fallback
};

/** "8:00 AM" -> "Day Shift", "9:00 PM" -> "Night Shift", based on check-in hour */
const getShiftType = (inTime: string | null) => {
  if (!inTime) return "";
  const [hm, meridiem] = inTime.split(" ");
  let [h] = hm.split(":").map(Number);
  if (meridiem === "PM" && h !== 12) h += 12;
  if (meridiem === "AM" && h === 12) h = 0;
  return h >= 6 && h < 18 ? "Day Shift" : "Night Shift";
};

/** Wage due for a single day, given the employee's rates. */
const calcDayPay = (emp: IMonthlyEmployee, day: IAttendanceDay) => {
  const regularHours = Math.min(day.workingTime, 8);
  const otHours = Math.max(0, day.workingTime - 8);
  const regularPay = regularHours * emp.summary.regularWageHour;
  const otPay = otHours * emp.summary.otWageHour;
  return {
    regularHours,
    otHours,
    regularPay: Math.round(regularPay),
    otPay: Math.round(otPay),
    total: Math.round(regularPay + otPay),
  };
};

/** Compute summary from a filtered list of attendance days */
const computeSummaryFromDays = (
  days: IAttendanceDay[],
  regularWageHour: number,
  otWageHour: number,
) => {
  let totalWorkingDays = 0;
  let totalHours = 0;
  let totalOTHours = 0;
  let regularHoursPayment = 0;
  let otPayment = 0;

  days.forEach((day) => {
    if (day.inTime) {
      totalWorkingDays++;
      totalHours += day.workingTime;
      const reg = Math.min(day.workingTime, 8);
      const ot = Math.max(0, day.workingTime - 8);
      totalOTHours += ot;
      regularHoursPayment += reg * regularWageHour;
      otPayment += ot * otWageHour;
    }
  });

  return {
    totalWorkingDays,
    totalHours: Math.round(totalHours * 10) / 10,
    totalOTHours: Math.round(totalOTHours * 10) / 10,
    regularHoursPayment: Math.round(regularHoursPayment * 100) / 100,
    otPayment: Math.round(otPayment * 100) / 100,
    netPayment: Math.round((regularHoursPayment + otPayment) * 100) / 100,
  };
};

// ─── Mock Data Generator ──────────────────────────────────────────────────

const SITES: ISite[] = [
  { id: "s1", name: "Site 4 — Roofing" },
  { id: "s2", name: "Site 2 — Block A" },
  { id: "s3", name: "Site 1 — Foundation" },
  { id: "s4", name: "Site 3 — Interior" },
];

const generateMockAttendance = (
  empId: string,
  year: number,
  month: number,
): IMonthlyEmployee => {
  const daysInMonth = getDaysInMonth(year, month);
  const attendance: IAttendanceDay[] = [];
  const names = [
    "Ramesh Kumar",
    "Sunita Devi",
    "Arvind Yadav",
    "Priya Sharma",
    "Vikram Singh",
    "Anita Patel",
  ];
  const designations = [
    "Mason",
    "Helper",
    "Mason",
    "Supervisor",
    "Helper",
    "Carpenter",
  ];
  const rates = [650, 600, 650, 750, 580, 620];
  const cycles = [15, 10, 30, 15, 10, 15];
  const idx = parseInt(empId) - 1;

  let totalHours = 0;
  let totalOTHours = 0;
  let totalWorkingDays = 0;

  for (let d = 1; d <= daysInMonth; d++) {
    const dateObj = new Date(year, month - 1, d);
    const dayOfWeek = dateObj.getDay();
    const isWeekend = dayOfWeek === 0;
    const isAbsent = !isWeekend && Math.random() < 0.08;

    let inTime: string | null = null;
    let outTime: string | null = null;
    let lunchBreak = 0;
    let workingTime = 0;
    let regularWorkTime = 0;
    let paid = false;

    if (!isWeekend && !isAbsent) {
      const inHour = 7 + Math.floor(Math.random() * 2);
      const inMin = Math.floor(Math.random() * 60);
      const outHour = 16 + Math.floor(Math.random() * 3);
      const outMin = Math.floor(Math.random() * 60);

      const to12Hour = (h: number) => (h % 12 === 0 ? 12 : h % 12);

      inTime = `${to12Hour(inHour)}:${String(inMin).padStart(2, "0")} ${inHour >= 12 ? "PM" : "AM"}`;
      outTime = `${to12Hour(outHour)}:${String(outMin).padStart(2, "0")} ${outHour >= 12 ? "PM" : "AM"}`;

      lunchBreak = 0.5 + Math.floor(Math.random() * 2) * 0.5;
      const totalHoursWorked = outHour + outMin / 60 - (inHour + inMin / 60);
      workingTime = Math.round((totalHoursWorked - lunchBreak) * 10) / 10;
      regularWorkTime = Math.min(workingTime, 8);
      const ot = Math.max(0, workingTime - 8);
      totalOTHours += ot;

      paid = Math.random() < 0.7;

      totalHours += workingTime;
      totalWorkingDays++;
    }

    attendance.push({
      date: d,
      inTime,
      outTime,
      lunchBreak,
      workingTime,
      regularWorkTime,
      paid,
    });
  }

  const regularWageHour = rates[idx] / 8;
  const otWageHour = regularWageHour * 1.5;
  const regularHoursPayment = regularWageHour * totalHours;
  const otPayment = otWageHour * totalOTHours;

  return {
    id: empId,
    name: names[idx % names.length],
    designation: designations[idx % designations.length],
    site: SITES[idx % SITES.length],
    dailyRate: rates[idx % rates.length],
    payCycleDays: cycles[idx % cycles.length],
    attendance,
    summary: {
      regularWageHour: Math.round(regularWageHour * 100) / 100,
      otWageHour: Math.round(otWageHour * 100) / 100,
      regularHoursPayment: Math.round(regularHoursPayment * 100) / 100,
      otPayment: Math.round(otPayment * 100) / 100,
      netPayment: Math.round((regularHoursPayment + otPayment) * 100) / 100,
      totalWorkingDays,
      totalHours: Math.round(totalHours * 10) / 10,
      totalOTHours: Math.round(totalOTHours * 10) / 10,
    },
  };
};

// ─── Day cell (colored card, like a shift block) ───────────────────────────

const DayCell: React.FC<{
  day: IAttendanceDay;
  designation?: string;
  isToday: boolean;
  isWeekend: boolean;
  onPayClick: () => void;
}> = ({ day, designation, isToday, isWeekend, onPayClick }) => {
  const cellBg = isToday
    ? "bg-teal-50/[0.15]"
    : isWeekend
      ? "bg-slate-100/40"
      : "bg-[#f8fafc]/70";

  // If absent or off day
  if (!day.inTime) {
    if (day.paid) {
      // Solid Dark Teal block representing Time off (paid) matching Sophia Martin style
      return (
        <td
          className={`p-1.5 text-center border-r border-slate-200 align-middle ${cellBg}`}
        >
          <div className="h-14 w-full bg-[#115e59] text-white rounded-lg p-2 flex flex-col items-start justify-center gap-0.5 text-left shadow-sm">
            <span className="text-[10px] font-bold leading-none">All day</span>
            <span className="text-[9.5px] opacity-90 leading-none flex items-center gap-1 mt-0.5">
              <Moon size={9} className="fill-white text-white shrink-0" />
              <span>Time off (paid)</span>
            </span>
          </div>
        </td>
      );
    }

    return (
      <td
        className={`p-1.5 text-center border-r border-slate-200 align-middle ${cellBg}`}
      >
        <div className="h-14 w-full flex items-center justify-center">
          <span className="text-slate-300 text-sm font-medium select-none">
            –
          </span>
        </div>
      </td>
    );
  }

  const isDue = !day.paid;
  const leftBorderColor = getDesignationColor(designation);

  return (
    <td
      className={`p-1.5 text-center border-r border-slate-200 align-middle ${cellBg}`}
    >
      <button
        type="button"
        disabled={!isDue}
        onClick={onPayClick}
        title={`${day.inTime} → ${day.outTime}${isDue ? " · click to pay" : ""}`}
        className={[
          "group relative w-full h-14 rounded-lg border shadow-sm p-1.5 flex flex-col items-start justify-between text-left transition-all",
          leftBorderColor,
          isDue
            ? "bg-amber-50/70 border-amber-200 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 cursor-pointer border-l-[3.5px]"
            : "bg-teal-50/50 border-teal-200 cursor-default opacity-95 border-l-[3.5px]",
        ].join(" ")}
      >
        {/* Paid / Due status chip */}
        <span
          className={[
            "absolute top-1 right-1 flex items-center gap-0.5 rounded px-1 py-[1px] text-[8px] font-bold uppercase tracking-wide leading-none",
            isDue ? "bg-amber-500 text-white" : "bg-teal-600 text-white",
          ].join(" ")}
        >
          {isDue ? "Due" : "Paid"}
        </span>

        {/* Shift Time Range */}
        <span className="text-[10.5px] font-bold text-slate-800 tabular-nums leading-none whitespace-nowrap pr-7">
          {compactTime(day.inTime)}–{compactTime(day.outTime)}
        </span>

        {/* Shift Type + Worked Hours */}
        <span className="text-[9.5px] font-semibold text-slate-500 truncate w-full leading-none flex items-center gap-1">
          <span className="truncate">{getShiftType(day.inTime)}</span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-400 font-medium shrink-0">
            {day.workingTime}h
          </span>
        </span>

        {/* Break and Payment Icon */}
        <div className="w-full flex items-center justify-between mt-0.5 leading-none">
          <span className="text-[9.5px] text-slate-400 font-medium flex items-center gap-1">
            <span className="text-[9px] font-semibold text-slate-400">||</span>
            <span>{formatBreak(day.lunchBreak)}</span>
          </span>
          {isDue ? (
            <div className="flex items-center gap-1 shrink-0">
              <Clock size={10} className="text-amber-500" />
              <Eye size={10} className="text-slate-300" />
            </div>
          ) : (
            <div className="flex items-center gap-1 shrink-0">
              <CheckCircle2 size={10} className="text-teal-500" />
            </div>
          )}
        </div>

        {isDue && (
          <span className="text-[9px] font-medium text-amber-700 opacity-0 group-hover:opacity-100 transition-opacity absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap bg-slate-800 text-white px-1.5 py-0.5 rounded z-30 pointer-events-none">
            Click to pay
          </span>
        )}
      </button>
    </td>
  );
};

// ─── Pay-day dialog (single day) ─────────────────────────────────────────

interface IPayTarget {
  employee: IMonthlyEmployee;
  day: IAttendanceDay;
  monthLabel: string;
}

const PayDayDialog: React.FC<{
  target: IPayTarget;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}> = ({ target, onClose, onConfirm, isSubmitting }) => {
  const { employee, day } = target;
  const pay = calcDayPay(employee, day);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
              {employee.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm leading-tight">
                {employee.name}
              </p>
              <p className="text-xs text-slate-400">
                {employee.designation || "—"} · {employee.site.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 rounded-md p-1 -mt-1 -mr-1"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">Date</span>
            <span className="text-sm font-medium text-slate-700">
              {target.monthLabel} {day.date}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">Punch in / out</span>
            <span className="text-sm font-medium text-slate-700 tabular-nums">
              {day.inTime} → {day.outTime}
            </span>
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">
                Regular ({pay.regularHours}h ×{" "}
                {inr(employee.summary.regularWageHour)})
              </span>
              <span className="font-medium text-slate-700 tabular-nums">
                {inr(pay.regularPay)}
              </span>
            </div>
            {pay.otHours > 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">
                  Overtime ({pay.otHours}h × {inr(employee.summary.otWageHour)})
                </span>
                <span className="font-medium text-slate-700 tabular-nums">
                  {inr(pay.otPay)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-slate-200">
              <span className="text-sm font-semibold text-slate-700">
                Amount due
              </span>
              <span className="text-lg font-bold text-primary tabular-nums">
                {inr(pay.total)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 pb-5 pt-1">
          <Button
            variant="outline"
            className="flex-1 rounded-xl border-slate-200"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 rounded-xl"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Wallet className="h-4 w-4 mr-2" />
            )}
            Pay {inr(pay.total)}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Bulk Pay Dialog (for a single employee, multiple days) ─────────────

interface IBulkPayTarget {
  employee: IMonthlyEmployee;
  days: IAttendanceDay[];
  monthLabel: string;
}

const BulkPayDialog: React.FC<{
  target: IBulkPayTarget;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}> = ({ target, onClose, onConfirm, isSubmitting }) => {
  const { employee, days } = target;
  const dueDays = days.filter((d) => d.inTime && !d.paid);
  const totalAmount = dueDays.reduce(
    (sum, d) => sum + calcDayPay(employee, d).total,
    0,
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
              {employee.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm leading-tight">
                {employee.name}
              </p>
              <p className="text-xs text-slate-400">
                {employee.designation || "—"} · {employee.site.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 rounded-md p-1 -mt-1 -mr-1"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">Days to pay</span>
            <span className="text-sm font-medium text-slate-700">
              {dueDays.length} day{dueDays.length > 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
            {dueDays.map((d) => (
              <span
                key={d.date}
                className="text-[11px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full"
              >
                {target.monthLabel} {d.date}
              </span>
            ))}
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">
                Total amount due
              </span>
              <span className="text-lg font-bold text-primary tabular-nums">
                {inr(totalAmount)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 pb-5 pt-1">
          <Button
            variant="outline"
            className="flex-1 rounded-xl border-slate-200"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 rounded-xl"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Wallet className="h-4 w-4 mr-2" />
            )}
            Pay All
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Multi‑Employee Bulk Pay Dialog ─────────────────────────────────────

interface IMultiBulkPayTarget {
  employees: IMonthlyEmployee[];
  monthLabel: string;
}

const MultiBulkPayDialog: React.FC<{
  target: IMultiBulkPayTarget;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}> = ({ target, onClose, onConfirm, isSubmitting }) => {
  const { employees, monthLabel } = target;

  const allDueDays = employees.flatMap((emp) =>
    emp.attendance
      .filter((d) => d.inTime && !d.paid)
      .map((d) => ({ emp, day: d })),
  );

  const totalAmount = allDueDays.reduce(
    (sum, { emp, day }) => sum + calcDayPay(emp, day).total,
    0,
  );

  const employeeGroups = employees
    .map((emp) => {
      const days = emp.attendance.filter((d) => d.inTime && !d.paid);
      const amount = days.reduce((sum, d) => sum + calcDayPay(emp, d).total, 0);
      return { emp, days, amount };
    })
    .filter((g) => g.days.length > 0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
              <Users size={18} />
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm leading-tight">
                Pay selected employees
              </p>
              <p className="text-xs text-slate-400">
                {employeeGroups.length} employee
                {employeeGroups.length > 1 ? "s" : ""} · {allDueDays.length} due
                day{allDueDays.length > 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 rounded-md p-1 -mt-1 -mr-1"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-80 overflow-y-auto">
          {employeeGroups.map(({ emp, days, amount }) => (
            <div
              key={emp.id}
              className="border-b border-slate-100 last:border-0 pb-3 last:pb-0"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {emp.name}
                  </p>
                  <p className="text-xs text-slate-400">
                    {days.length} day{days.length > 1 ? "s" : ""}
                  </p>
                </div>
                <span className="text-sm font-semibold text-primary tabular-nums">
                  {inr(amount)}
                </span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {days.map((d) => (
                  <span
                    key={d.date}
                    className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full"
                  >
                    {monthLabel} {d.date}
                  </span>
                ))}
              </div>
            </div>
          ))}
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 mt-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">
                Total amount due
              </span>
              <span className="text-lg font-bold text-primary tabular-nums">
                {inr(totalAmount)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 pb-5 pt-1">
          <Button
            variant="outline"
            className="flex-1 rounded-xl border-slate-200"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 rounded-xl"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Wallet className="h-4 w-4 mr-2" />
            )}
            Pay All
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────

const WageEmployees: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [employees, setEmployees] = useState<IMonthlyEmployee[]>([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");
  const [siteIds, setSiteIds] = useState<Set<string>>(new Set());
  const [isSiteFilterOpen, setIsSiteFilterOpen] = useState(false);

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  // ── Date range state ──────────────────────────────────────────────────
  const today = new Date();
  const defaultStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const defaultEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const [startDate, setStartDate] = useState<Date>(defaultStart);
  const [endDate, setEndDate] = useState<Date>(defaultEnd);

  const currentMonth = startDate.getMonth() + 1;
  const currentYear = startDate.getFullYear();

  // Pay-day dialog state (single day)
  const [payTarget, setPayTarget] = useState<IPayTarget | null>(null);
  const [isPaying, setIsPaying] = useState(false);

  // Bulk pay dialog state (single employee)
  const [bulkPayTarget, setBulkPayTarget] = useState<IBulkPayTarget | null>(
    null,
  );
  const [isBulkPaying, setIsBulkPaying] = useState(false);

  // Multi‑employee bulk pay state
  const [multiBulkPayTarget, setMultiBulkPayTarget] =
    useState<IMultiBulkPayTarget | null>(null);
  const [isMultiBulkPaying, setIsMultiBulkPaying] = useState(false);

  // Selection state (Set of employee IDs)
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(
    new Set(),
  );

  const monthName = getMonthName(currentMonth);
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);

  const visibleDayNumbers = useMemo(() => {
    const days: number[] = [];
    const start = startDate.getDate();
    const end = endDate.getDate();
    for (let d = start; d <= end; d++) {
      if (d >= 1 && d <= daysInMonth) days.push(d);
    }
    return days;
  }, [startDate, endDate, daysInMonth]);

  const isCurrentMonthInView =
    today.getFullYear() === currentYear &&
    today.getMonth() + 1 === currentMonth;
  const todayDate = isCurrentMonthInView ? today.getDate() : -1;

  // ── Fetch employees (mock) ────────────────────────────────────────────
  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      // Fetch mock monthly data
      const mockData: IMonthlyEmployee[] = [];
      for (let i = 1; i <= 6; i++) {
        mockData.push(
          generateMockAttendance(String(i), currentYear, currentMonth),
        );
      }
      setEmployees(mockData);
      setPaginationData((prev) => ({
        ...prev,
        total: mockData.length,
        totalPages: 1,
      }));
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, currentYear]);

  // ── Filter employees by search/site/sort (client-side) ──────────────
  const filteredEmployees = useMemo(() => {
    const q = search.toLowerCase();
    let list = employees.filter((emp) => {
      const matchesSearch = !q || emp.name.toLowerCase().includes(q);
      const matchesSite = siteIds.size === 0 || siteIds.has(emp.site.id);
      return matchesSearch && matchesSite;
    });

    switch (sort) {
      case "name":
        list = [...list].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "netPaymentDesc":
        list = [...list].sort(
          (a, b) => b.summary.netPayment - a.summary.netPayment,
        );
        break;
      case "hoursDesc":
        list = [...list].sort(
          (a, b) => b.summary.totalHours - a.summary.totalHours,
        );
        break;
      default:
        break;
    }
    return list;
  }, [employees, search, siteIds, sort]);

  // ── Compute per-employee filtered attendance and summary ─────────────
  const visibleEmployees = useMemo(() => {
    return filteredEmployees.map((emp) => {
      const filteredDays = emp.attendance.filter((d) =>
        visibleDayNumbers.includes(d.date),
      );
      const summary = computeSummaryFromDays(
        filteredDays,
        emp.summary.regularWageHour,
        emp.summary.otWageHour,
      );
      return {
        ...emp,
        attendance: filteredDays,
        summary: {
          ...emp.summary,
          ...summary,
        },
      };
    });
  }, [filteredEmployees, visibleDayNumbers]);

  // ── Stat cards ────────────────────────────────────────────────────────
  const totalEmployees = visibleEmployees.length;
  const totalHours = visibleEmployees.reduce(
    (acc, e) => acc + e.summary.totalHours,
    0,
  );
  const paidDays = visibleEmployees.reduce(
    (acc, e) => acc + e.attendance.filter((d) => d.paid).length,
    0,
  );
  const netPayable = visibleEmployees.reduce(
    (acc, e) => acc + e.summary.netPayment,
    0,
  );

  // ── Selection helpers ────────────────────────────────────────────────

  const toggleEmployeeSelection = (id: string) => {
    setSelectedEmployees((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    const selectableIds = visibleEmployees
      .filter((emp) => emp.attendance.some((d) => d.inTime && !d.paid))
      .map((emp) => emp.id);
    const allSelected = selectableIds.every((id) => selectedEmployees.has(id));
    if (allSelected) {
      setSelectedEmployees(new Set());
    } else {
      const newSet = new Set(selectedEmployees);
      selectableIds.forEach((id) => newSet.add(id));
      setSelectedEmployees(newSet);
    }
  };

  const isAllSelected = (() => {
    const selectableIds = visibleEmployees
      .filter((emp) => emp.attendance.some((d) => d.inTime && !d.paid))
      .map((emp) => emp.id);
    return (
      selectableIds.length > 0 &&
      selectableIds.every((id) => selectedEmployees.has(id))
    );
  })();

  const isSomeSelected = (() => {
    const selectableIds = visibleEmployees
      .filter((emp) => emp.attendance.some((d) => d.inTime && !d.paid))
      .map((emp) => emp.id);
    return selectableIds.some((id) => selectedEmployees.has(id));
  })();

  const selectedEmployeesWithDue = visibleEmployees.filter(
    (emp) =>
      selectedEmployees.has(emp.id) &&
      emp.attendance.some((d) => d.inTime && !d.paid),
  );

  // ── Handlers ──────────────────────────────────────────────────────────

  const toggleSiteFilter = (id: string) => {
    setSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleDateRangeChange = (start: string, end: string) => {
    const newStart = new Date(start);
    const newEnd = new Date(end);
    if (!isNaN(newStart.getTime()) && !isNaN(newEnd.getTime())) {
      setStartDate(newStart);
      setEndDate(newEnd);
      setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    }
  };

  const setQuickRange = (days: number | null) => {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last =
      days === null
        ? new Date(today.getFullYear(), today.getMonth() + 1, 0)
        : new Date(today.getFullYear(), today.getMonth(), days);
    setStartDate(first);
    setEndDate(last);
  };

  const goToPrevMonth = () => {
    const first = new Date(currentYear, currentMonth - 2, 1);
    const last = new Date(currentYear, currentMonth - 1, 0);
    setStartDate(first);
    setEndDate(last);
  };

  const goToNextMonth = () => {
    const first = new Date(currentYear, currentMonth, 1);
    const last = new Date(currentYear, currentMonth + 1, 0);
    setStartDate(first);
    setEndDate(last);
  };

  const goToToday = () => setQuickRange(null);

  // Single day pay
  const handleDayPayClick = (
    employee: IMonthlyEmployee,
    day: IAttendanceDay,
  ) => {
    if (!day.inTime || day.paid) return;
    setPayTarget({ employee, day, monthLabel: monthName });
  };

  const handleConfirmDayPay = async () => {
    if (!payTarget) return;
    setIsPaying(true);
    try {
      await new Promise((res) => setTimeout(res, 500));
      setEmployees((prev) =>
        prev.map((emp) =>
          emp.id !== payTarget.employee.id
            ? emp
            : {
                ...emp,
                attendance: emp.attendance.map((d) =>
                  d.date === payTarget.day.date ? { ...d, paid: true } : d,
                ),
              },
        ),
      );
      setPayTarget(null);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsPaying(false);
    }
  };

  // Single‑employee bulk pay
  const handleBulkPayClick = (employee: IMonthlyEmployee) => {
    const dueDays = employee.attendance.filter((d) => d.inTime && !d.paid);
    if (dueDays.length === 0) return;
    setBulkPayTarget({ employee, days: dueDays, monthLabel: monthName });
  };

  const handleConfirmBulkPay = async () => {
    if (!bulkPayTarget) return;
    setIsBulkPaying(true);
    try {
      await new Promise((res) => setTimeout(res, 500));
      const dayNumbers = bulkPayTarget.days.map((d) => d.date);
      setEmployees((prev) =>
        prev.map((emp) =>
          emp.id !== bulkPayTarget.employee.id
            ? emp
            : {
                ...emp,
                attendance: emp.attendance.map((d) =>
                  dayNumbers.includes(d.date) ? { ...d, paid: true } : d,
                ),
              },
        ),
      );
      setBulkPayTarget(null);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsBulkPaying(false);
    }
  };

  // Multi‑employee bulk pay
  const handleMultiBulkPayClick = () => {
    if (selectedEmployeesWithDue.length === 0) return;
    setMultiBulkPayTarget({
      employees: selectedEmployeesWithDue,
      monthLabel: monthName,
    });
  };

  const handleConfirmMultiBulkPay = async () => {
    if (!multiBulkPayTarget) return;
    setIsMultiBulkPaying(true);
    try {
      await new Promise((res) => setTimeout(res, 500));
      const updatedEmployees = employees.map((emp) => {
        const targetEmp = multiBulkPayTarget.employees.find(
          (e) => e.id === emp.id,
        );
        if (!targetEmp) return emp;
        const dayNumbers = targetEmp.attendance
          .filter((d) => d.inTime && !d.paid)
          .map((d) => d.date);
        return {
          ...emp,
          attendance: emp.attendance.map((d) =>
            dayNumbers.includes(d.date) ? { ...d, paid: true } : d,
          ),
        };
      });
      setEmployees(updatedEmployees);
      setSelectedEmployees(new Set());
      setMultiBulkPayTarget(null);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsMultiBulkPaying(false);
    }
  };

  // ─── Dynamic Grid Calculations for Bottom Summary Rows ──────────────────

  /** Sum total working hours for a given day across all employees */
  const getDayTotalHours = (dayNum: number) => {
    return visibleEmployees.reduce((sum, emp) => {
      const day = emp.attendance.find((d) => d.date === dayNum);
      return sum + (day?.workingTime || 0);
    }, 0);
  };

  /** Sum total calculated cost/wages for a given day across all employees */
  const getDayTotalCost = (dayNum: number) => {
    return visibleEmployees.reduce((sum, emp) => {
      const day = emp.attendance.find((d) => d.date === dayNum);
      if (!day || !day.inTime) return sum;
      return sum + calcDayPay(emp, day).total;
    }, 0);
  };

  /** Mock Revenue mapping based on day of week to match design image */
  const getRevenueForDay = (dayNum: number) => {
    const dateObj = new Date(currentYear, currentMonth - 1, dayNum);
    const dayOfWeek = dateObj.getDay();
    if (dayOfWeek === 0) return 0; // Sunday
    if (dayOfWeek === 6) return 6500; // Saturday
    const baseRevenues = [0, 3800, 4000, 4000, 5250, 6000, 6500];
    return baseRevenues[dayOfWeek];
  };

  /** Calculate labor percentage: (total wages / revenue) * 100 */
  const getLaborPercentage = (cost: number, revenue: number) => {
    if (revenue === 0) return "0%";
    const percentage = (cost / revenue) * 100;
    return `${Math.round(percentage * 10) / 10}%`;
  };

  // ─── Non-null locals for dialogs (fixes TS narrowing issue in JSX) ────
  const activePayTarget = payTarget;
  const activeBulkPayTarget = bulkPayTarget;
  const activeMultiBulkPayTarget = multiBulkPayTarget;

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className="w-full space-y-6">
      {/* Redesigned Header Bar: Title + Date select + Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <MainHeading title="Monthly Salary Report" />
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-sm font-bold text-slate-700">
              {monthName} {currentYear}
            </span>
            <span className="text-xs text-slate-400">
              · {visibleDayNumbers.length} days shown
            </span>
            {/* Horizontal Status Legend Inline */}
            <div className="hidden lg:flex items-center gap-3.5 border-l border-slate-200 pl-4 ml-2">
              <span className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                <span className="h-2 w-2 rounded-sm bg-teal-600" /> Paid Shift
              </span>
              <span className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                <span className="h-2 w-2 rounded-sm bg-amber-500" /> Due Shift
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Stat Cards Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-xs">
          <div className="flex items-center gap-2 text-slate-400 mb-1">
            <Users size={15} />
            <span className="text-xs font-medium">Employees</span>
          </div>
          <p className="text-xl font-bold text-slate-800 tabular-nums">
            {totalEmployees}
          </p>
        </div>
        <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-xs">
          <div className="flex items-center gap-2 text-slate-400 mb-1">
            <Clock size={15} />
            <span className="text-xs font-medium">Hours worked</span>
          </div>
          <p className="text-xl font-bold text-slate-800 tabular-nums">
            {totalHours.toFixed(1)}h
          </p>
        </div>
        <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-xs">
          <div className="flex items-center gap-2 text-slate-400 mb-1">
            <CheckCircle2 size={15} />
            <span className="text-xs font-medium">Paid days</span>
          </div>
          <p className="text-xl font-bold text-slate-800 tabular-nums">
            {paidDays}
          </p>
        </div>
        <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-xs">
          <div className="flex items-center gap-2 text-slate-400 mb-1">
            <DollarSign size={15} />
            <span className="text-xs font-medium">Net payable</span>
          </div>
          <p className="text-xl font-bold text-primary tabular-nums">
            {inr(netPayable)}
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm p-4 sm:p-5 space-y-4">
        {/* Modern Filter Toolbar: Left aligned Search/Sites/Sort, Right aligned Date selection to prevent overlap */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-50 border border-slate-200 rounded-xl p-3.5">
          {/* Left Side: Search & Filter selections */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search Box */}
            <div className="relative w-64">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search employee..."
                className="w-full h-9 pl-9 pr-3 text-xs bg-white rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder:text-slate-400"
              />
            </div>

            {/* Site Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsSiteFilterOpen(!isSiteFilterOpen)}
                className="flex items-center gap-2 h-9 px-3 text-xs bg-white border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium transition-all"
              >
                <span>Sites ({siteIds.size === 0 ? "All" : siteIds.size})</span>
                <ChevronDown size={14} className="text-slate-400" />
              </button>
              {isSiteFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setIsSiteFilterOpen(false)}
                  />
                  <div className="absolute left-0 mt-1.5 w-56 bg-white border border-slate-200 rounded-lg shadow-md py-1.5 z-40">
                    {SITES.map((site) => {
                      const checked = siteIds.has(site.id);
                      return (
                        <button
                          key={site.id}
                          onClick={() => toggleSiteFilter(site.id)}
                          className="w-full flex items-center gap-2 text-left rounded-md px-3 py-1.5 hover:bg-slate-50 text-xs text-slate-600 font-medium"
                        >
                          <span
                            className={`flex items-center justify-center h-3.5 w-3.5 rounded border shrink-0 ${
                              checked
                                ? "bg-primary border-primary"
                                : "bg-white border-slate-300"
                            }`}
                          >
                            {checked && (
                              <Check size={10} className="text-white" />
                            )}
                          </span>
                          <span className="truncate">{site.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 h-9">
              <span className="text-[11px] text-slate-400 font-medium pl-1 shrink-0">
                Sort
              </span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="h-full bg-transparent text-xs font-semibold focus:outline-none text-slate-600 pr-2 cursor-pointer"
              >
                <option value="">Default</option>
                <option value="name">Name (A–Z)</option>
                <option value="netPaymentDesc">Net payment (High–Low)</option>
                <option value="hoursDesc">Hours worked (High–Low)</option>
              </select>
            </div>
          </div>

          {/* Right Side: Date Filter controls positioned separately to prevent overlapping */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Picker (w-60 to give date text ample space and prevent text wrap/overlap) */}
            <div className="w-60 shrink-0">
              <DateRangePicker
                startDate={startDate.toISOString().slice(0, 10)}
                endDate={endDate.toISOString().slice(0, 10)}
                onChange={handleDateRangeChange}
                placeholder="Select range"
              />
            </div>
          </div>
        </div>

        {/* Full-width Calendar Table */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <th className="sticky left-0 z-20 bg-slate-50 px-2 py-3 text-center border-r border-slate-200 min-w-[40px] shadow-[2px_0_4px_rgba(0,0,0,0.03)]">
                    <button
                      type="button"
                      onClick={toggleSelectAll}
                      className="flex items-center justify-center w-5 h-5 rounded border border-slate-300 bg-white hover:bg-slate-50 transition-colors mx-auto"
                    >
                      {isAllSelected ? (
                        <Check className="h-4 w-4 text-primary" />
                      ) : isSomeSelected ? (
                        <Square className="h-4 w-4 text-slate-400" />
                      ) : (
                        <Square className="h-4 w-4 text-slate-300" />
                      )}
                    </button>
                  </th>
                  <th className="sticky left-[40px] z-20 bg-slate-50 px-4 py-3 text-left border-r border-slate-200 min-w-[240px] shadow-[2px_0_4px_rgba(0,0,0,0.03)]">
                    <button
                      type="button"
                      onClick={() => setSort(sort === "name" ? "" : "name")}
                      className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      <span>Employee</span>
                      <span className="text-[10px] text-slate-400 font-normal">
                        {sort === "name"
                          ? "↑ First name (A-Z)"
                          : "↓ Sort (Name)"}
                      </span>
                    </button>
                  </th>
                  {visibleDayNumbers.map((day) => {
                    const dateObj = new Date(
                      currentYear,
                      currentMonth - 1,
                      day,
                    );
                    const isWeekend = dateObj.getDay() === 0;
                    const isToday = day === todayDate;
                    const weekdayStr = dateObj.toLocaleString("default", {
                      weekday: "short",
                    });

                    return (
                      <th
                        key={day}
                        className={`px-2 py-3.5 text-center border-r border-slate-200 last:border-r-0 min-w-[130px] ${
                          isToday
                            ? "text-[#0f766e] bg-[#f0fdfa] font-bold"
                            : isWeekend
                              ? "text-slate-400 bg-slate-50/10 font-normal"
                              : "text-[#0f766e] font-semibold"
                        }`}
                      >
                        <span className="text-xs tabular-nums leading-none">
                          {weekdayStr} {day}
                        </span>
                      </th>
                    );
                  })}
                  <th className="sticky right-0 z-20 bg-slate-50 px-3 py-3.5 text-center font-semibold text-slate-600 border-l border-slate-200 min-w-[120px] shadow-[-2px_0_4px_rgba(0,0,0,0.03)]">
                    Total / Pay
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td
                        className="sticky left-0 z-10 bg-white px-2 py-3"
                        colSpan={2}
                      >
                        <div className="h-8 bg-slate-100 rounded-lg animate-pulse w-32" />
                      </td>
                      {visibleDayNumbers.map((_, j) => (
                        <td key={j} className="px-1 py-3">
                          <div className="h-8 bg-slate-50 rounded-lg animate-pulse" />
                        </td>
                      ))}
                      <td className="sticky right-0 z-10 bg-white px-2 py-3">
                        <div className="h-8 bg-slate-100 rounded-lg animate-pulse" />
                      </td>
                    </tr>
                  ))
                ) : visibleEmployees.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleDayNumbers.length + 3}
                      className="py-14 text-center text-slate-400"
                    >
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm font-medium text-slate-500">
                        No employees found
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Try a different name or site for {monthName}{" "}
                        {currentYear}
                      </p>
                    </td>
                  </tr>
                ) : (
                  visibleEmployees.map((emp, idx) => {
                    const stickyBg = idx % 2 === 0 ? "bg-white" : "bg-slate-50";
                    const dueDays = emp.attendance.filter(
                      (d) => d.inTime && !d.paid,
                    );
                    const hasDue = dueDays.length > 0;
                    const isSelected = selectedEmployees.has(emp.id);

                    return (
                      <tr
                        key={emp.id}
                        className={`border-b border-slate-200 hover:bg-slate-100/60 transition-colors ${stickyBg}`}
                      >
                        {/* Checkbox */}
                        <td
                          className={`sticky left-0 z-10 ${stickyBg} px-2 py-2 text-center border-r border-slate-200 shadow-[2px_0_4px_rgba(0,0,0,0.03)]`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={!hasDue}
                            onChange={() => toggleEmployeeSelection(emp.id)}
                            className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/20 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </td>

                        {/* Employee Card Info */}
                        <td
                          className={`sticky left-[40px] z-10 ${stickyBg} px-4 py-2 border-r border-slate-200 shadow-[2px_0_4px_rgba(0,0,0,0.03)]`}
                        >
                          <div className="flex flex-col items-start gap-1">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div
                                className={[
                                  "h-8 w-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 shadow-sm border border-slate-200",
                                  idx % 3 === 0
                                    ? "bg-teal-50 text-teal-700"
                                    : idx % 3 === 1
                                      ? "bg-indigo-50 text-indigo-700"
                                      : "bg-amber-50 text-amber-700",
                                ].join(" ")}
                              >
                                {emp.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0 text-left">
                                <p className="font-semibold text-slate-800 hover:text-[#0f766e] truncate text-sm leading-tight cursor-pointer">
                                  {emp.name}
                                </p>
                                <p className="flex items-center gap-1 text-[10.5px] text-slate-400 truncate mt-0.5">
                                  <MapPin size={9} className="shrink-0" />
                                  <span className="truncate">
                                    {emp.site.name}
                                  </span>
                                  <span className="text-slate-300">·</span>
                                  <span className="font-medium text-slate-500 shrink-0">
                                    {inr(emp.dailyRate)}/day
                                  </span>
                                </p>
                              </div>
                            </div>
                            {/* Indented soft-green hours pill under the name */}
                            <span
                              className={[
                                "ml-[42px] px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 tabular-nums shadow-sm border",
                                emp.summary.totalHours > 40
                                  ? "bg-rose-50 text-rose-700 border-rose-200"
                                  : emp.summary.totalHours > 30
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                    : "bg-slate-100 text-slate-600 border-slate-200",
                              ].join(" ")}
                            >
                              {emp.summary.totalHours}h
                            </span>
                          </div>
                        </td>

                        {/* Attendance cells */}
                        {emp.attendance.map((day) => (
                          <DayCell
                            key={day.date}
                            day={day}
                            designation={emp.designation}
                            isToday={day.date === todayDate}
                            isWeekend={
                              new Date(
                                currentYear,
                                currentMonth - 1,
                                day.date,
                              ).getDay() === 0
                            }
                            onPayClick={() => handleDayPayClick(emp, day)}
                          />
                        ))}

                        {/* Total / Pay Summary */}
                        <td
                          className={`sticky right-0 z-10 ${stickyBg} px-3 py-2 text-center border-l border-slate-200 shadow-[-2px_0_4px_rgba(0,0,0,0.03)]`}
                        >
                          <div className="flex flex-col items-center text-xs gap-1">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-slate-700">
                                {emp.summary.totalWorkingDays}d
                              </span>
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-500 font-medium">
                                {emp.summary.totalHours}h
                              </span>
                            </div>
                            <span className="font-bold text-[#0f766e] text-xs">
                              {inr(emp.summary.netPayment)}
                            </span>
                            {hasDue && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-1 h-7 px-2 text-[10px] rounded-lg border-amber-200 text-amber-700 bg-amber-50/50 hover:bg-amber-50 hover:text-amber-800 transition-colors shadow-sm font-semibold"
                                onClick={() => handleBulkPayClick(emp)}
                              >
                                Pay {dueDays.length} due
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-slate-100">
            <TablePagination
              paginationData={paginationData}
              setPaginationData={setPaginationData}
              handleChangePageSize={(value) =>
                setPaginationData((prev) => ({
                  ...prev,
                  pageSize: value,
                  currentPage: 1,
                }))
              }
              isLoading={isLoading}
            />
          </div>
        </div>
        {/* </div> */}
      </div>

      {/* Single-day pay dialog */}
      {activePayTarget && (
        <PayDayDialog
          target={activePayTarget}
          onClose={() => (isPaying ? null : setPayTarget(null))}
          onConfirm={handleConfirmDayPay}
          isSubmitting={isPaying}
        />
      )}

      {/* Single‑employee bulk pay dialog */}
      {activeBulkPayTarget && (
        <BulkPayDialog
          target={activeBulkPayTarget}
          onClose={() => (isBulkPaying ? null : setBulkPayTarget(null))}
          onConfirm={handleConfirmBulkPay}
          isSubmitting={isBulkPaying}
        />
      )}

      {/* Multi‑employee bulk pay dialog */}
      {activeMultiBulkPayTarget && (
        <MultiBulkPayDialog
          target={activeMultiBulkPayTarget}
          onClose={() =>
            isMultiBulkPaying ? null : setMultiBulkPayTarget(null)
          }
          onConfirm={handleConfirmMultiBulkPay}
          isSubmitting={isMultiBulkPaying}
        />
      )}
    </div>
  );
};

export default WageEmployees;
